// demo-content-provider.js — JSON-backed content for the L2B demo (Brief 003).
//
// Replaces KFE Web's sql.js / mmu-loader / `services` plumbing with a thin
// reader over `content/demo-mmus.json` (Brief 002). That file holds the 5
// exercises (4 training + Testing) and an 87-entry MMU dictionary; this module
// fetches it once, caches it, and inflates each referenced MMU into the `Mmu`
// shape the COPIED-VERBATIM engines expect (so `mmusToItemData()` consumes our
// MMUs unchanged).
//
// MMU order within an exercise is the pedagogical contract (Brief 003 §4) — we
// preserve `mmuIds` array order and never re-sort.

import { stem } from "./media-paths.js";

/** @typedef {import("./mmu-loader.js").Mmu} Mmu */

/**
 * @typedef {object} RawMmu
 * @property {string} phrase
 * @property {string} [phraseZh]
 * @property {string} imageStem
 * @property {string} audioStem
 * @property {boolean} [isLargeImage]
 */

/**
 * @typedef {object} RawExercise
 * @property {number} itemId
 * @property {"training"|"testing"} mode
 * @property {string} label
 * @property {string} title
 * @property {string} [titleZh]
 * @property {number[]} mmuIds
 * @property {number[]} [mmuIdsFull13]  Brief 020: 103461's parked thirteen. Data
 *   only — nothing reads it at runtime; `mmuIds` is the run.
 */

/**
 * @typedef {object} DemoContent
 * @property {RawExercise[]} exercises
 * @property {Record<string, RawMmu>} mmus
 */

/**
 * @typedef {object} Exercise
 * @property {number} itemId
 * @property {string} title
 * @property {string} titleZh
 * @property {string} label
 * @property {"training"|"testing"} mode
 * @property {Mmu[]} mmus   resolved + ordered (pedagogical sequence preserved)
 */

/** @type {DemoContent | null} */ let content = null;
/** @type {Promise<DemoContent> | null} */ let inFlight = null;

/**
 * Fetch + cache `content/demo-mmus.json`. Idempotent and safe to call from
 * every screen's `onConnected`; concurrent callers join the same in-flight
 * promise. Origin-rooted path works from any route.
 * @returns {Promise<DemoContent>}
 */
export async function loadContent() {
  if (content) return content;
  if (inFlight) return inFlight;
  inFlight = fetch("/content/demo-mmus.json")
    .then((r) => {
      if (!r.ok) throw new Error(`[content] demo-mmus.json HTTP ${r.status}`);
      return r.json();
    })
    .then((json) => {
      content = /** @type {DemoContent} */ (json);
      return content;
    })
    .finally(() => { inFlight = null; });
  return inFlight;
}

/**
 * Inflate a raw MMU dictionary entry into the engine's `Mmu` shape. Media stems
 * come PRE-CASED from JSON — copied verbatim so R2's case-sensitive keys resolve
 * (Brief 003 §5). `imageFileName`/`audioFileName` are intentionally left empty:
 * the demo resolves media by stem, never by literal filename, and an empty
 * filename keeps `applyL2bDefensiveFilters`'s `.jpg` guard a no-op.
 * @param {number} mmuId
 * @param {RawMmu} raw
 * @returns {Mmu}
 */
function inflate(mmuId, raw) {
  return {
    mmuId,
    phrase: raw.phrase,
    phraseZh: raw.phraseZh ?? "",
    imageFileName: "",
    audioFileName: "",
    isHaai: false,
    isL2b: true,
    isSelfRecord: false,
    isLargeImage: !!raw.isLargeImage,
    masteryTarget: 0,
    wordKey: stem(raw.phrase),
    audioStem: raw.audioStem,
    imageStem: raw.imageStem,
  };
}

/** All exercises, in source order (drives the dashboard). Requires loadContent() first. */
export function getExercises() {
  if (!content) throw new Error("[content] not loaded — call loadContent() first");
  return content.exercises;
}

/**
 * Resolve one exercise with its MMUs inflated + ordered. Returns null for an
 * unknown itemId. Requires loadContent() first.
 * @param {number|string} itemId
 * @returns {Exercise | null}
 */
export function getExercise(itemId) {
  if (!content) throw new Error("[content] not loaded — call loadContent() first");
  const id = Number(itemId);
  const ex = content.exercises.find((e) => e.itemId === id);
  if (!ex) return null;
  const mmus = [];
  for (const mmuId of ex.mmuIds) {
    const raw = content.mmus[String(mmuId)];
    if (!raw) continue; // tolerate a stray id missing from the dictionary
    mmus.push(inflate(mmuId, raw));
  }
  return {
    itemId: ex.itemId,
    title: ex.title,
    titleZh: ex.titleZh ?? "",
    label: ex.label,
    mode: ex.mode,
    mmus,
  };
}
